import * as THREE from 'three';
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader';
import {Clock} from './clock';
import assets from './assets';
import {vec3, mat4} from 'gl-matrix';
import {lerp, smoothstep} from './utils';

const {min, max, asin, atan2, PI} = Math;

function loadGLTF(url) {
    return new Promise(resolve => {
        const loader = new GLTFLoader;
        loader.load(url, gltf => resolve(gltf));
    });
}

export class PenHerder {
    scene = null;
    penTypes = ['brush', 'bullet'];
    gltf = null;
    pens = [];
    clock = new Clock;

    constructor() {
        this.createScene();
    }

    async createScene() {
        this.scene = new THREE.Scene;

        // lighting
        const light = new THREE.PointLight(0xffffff, 300, 0);
        light.position.set(0, 0, 0);
        this.scene.add(light);

        this.gltf = await loadGLTF(assets.models.pens);
    }

    isAnimatedPenType(type) {
        return this.penTypes.includes(type);
    }

    getPen(owner, type) {
        if (!this.isAnimatedPenType(type))
            return null;

        for (let pen of this.pens) {
            if (pen._penType === type &&
                pen._penOwner === owner)
            {
                return pen;
            }
        }

        // create new pen
        const pen = new Pen({
            gltf: this.gltf,
            owner,
            type,
        });
        this.pens.push(pen);
        this.scene.add(pen);
        return pen;
    }

    update(engine) {
        if (!this.gltf)
            return;

        // mark all as inactive
        for (let pen of this.pens)
            pen._penActive = false;

        const {penCount} = engine;
        for (let i = 0; i < penCount; ++i) {
            const penDynamics = engine.getPenDynamics(i);
            const pen = this.getPen(i, penDynamics.type);
            if (pen) {
                // guard because may have returned null for eraser/none types
                pen.updateDynamics(penDynamics);
            }
        }

        const deltaTime = this.clock.update();
        for (let pen of this.pens)
            pen.update(deltaTime);
    }
}

const findObjectByName = (root, name) => findObject(root, o => o.name == name);
const findObjectByMaterialName = (root, name) => findObject(root, o => o.material?.name == name);
// const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();

class Pen extends THREE.Group {

    _penOwner = 0;
    _penType = 'none';
    _penColor = vec3.create();

    _penDir = vec3.create();
    _penTiltXYRotate = vec3.create();

    _penDirTarget = vec3.create();
    _penTiltXYRotateTarget = vec3.create();

    _penOpacity = 0;
    _penActive = false;
    _penLift = 0;
    _penLiftTarget = 0;
    _penPressure = 0;

    constructor({
        gltf,
        type,
        owner,
    }) {
        super();

        this._penType = type;
        this._penOwner = owner;

        let originalName;
        if (type == 'brush') {
            originalName = 'ob_sharpie_brush';
        } else if (type == 'bullet') {
            originalName = 'ob_sharpie_bullet';
        } else {
            originalName = 'ob_pencil';
        }

        // clone the original
        const original = findObjectByName(gltf.scene, originalName);
        this.copy(original, true);

        // clone the materials
        this._penMaterials = [];
        for (let ob of this.children) {
            ob.material = ob.material.clone();
            ob.material.transparent = true;
            // if (ob.material.map)
            //     ob.material.map.anisotropy = maxAnisotropy;

            ob.material._isPenColor = [
                'ma_sharpie_bullet_cap',
                'ma_sharpie_bullet_nib',
                'ma_sharpie_brush_color',
            ].includes(ob.material.name);
            //console.log('name=%s opacity=%f side=%d', ob.material.name, ob.material.opacity, ob.material.side);

            if (ob.material.name == 'ma_sharpie_brush_clear') {
                // fix the brush clear plastic material to read better
                ob.material.opacity = 0.657;
                ob.material.metalness = 0.846;
                ob.material.roughness = 0.033;
            }

            ob.material._defaultOpacity = ob.material.opacity;

            this._penMaterials.push(ob.material);
        }

        // we'll set matrix directly
        this.matrixAutoUpdate = false;
    }

    updateDynamics(penDynamics) {
        const {dir, color, active, pressure} = penDynamics;
        vec3.copy(this._penColor, color);
        vec3.copy(this._penDirTarget, dir);
        vec3.set(this._penTiltXYRotateTarget,
            penDynamics.tiltX,
            penDynamics.tiltY,
            penDynamics.rotate);
        if (this._penOpacity < .5) {
            // transition to active: go there immediately
            vec3.copy(this._penDir, this._penDirTarget);
            vec3.copy(this._penTiltXYRotate, this._penTiltXYRotateTarget);
        }
        this._penActive = active;
        this._penPressure = pressure;
    }

    update(deltaTime) {
        // opacity
        if (this._penActive) {
            this._penOpacity = 1;
            const minLift = -0.3 * this._penPressure;
            this._penLiftTarget = max(minLift, this._penLift - (deltaTime / 30));
        } else {
            const fadeDuration = 700;
            const delta = deltaTime / fadeDuration;
            this._penOpacity = max(0, this._penOpacity - delta);
            this._penLiftTarget = min(1, this._penLift + (deltaTime / 200));
        }

        // visibility
        this.visible = this._penOpacity > 0 ? true : false;

        if (this._penType === 'eraser') {
            // don't draw anything for eraser
            this.visible = false;
        }

        if (this.visible) {
            const lerpAmount = (this._penOwner === 0) ? 1 : 0.3;
            const u = lerpAmount * (deltaTime / 16.7);
            vec3.lerp(this._penDir, this._penDir, this._penDirTarget, u);
            vec3.lerp(this._penTiltXYRotate, this._penTiltXYRotate, this._penTiltXYRotateTarget, u);
            this._penLift = lerp(this._penLift, this._penLiftTarget, u);

            // materials
            this.updateMaterials(this._penColor, this._penOpacity);

            // matrix
            calcPenMatrix(this.matrix.elements, this._penDir, this._penTiltXYRotate, this._penLift);
        }
    }

    updateMaterials(color, opacity) {
        for (let m of this._penMaterials) {
            if (m._defaultOpacity === 1) {
                m.opacity = opacity;
            } else {
                // trying to deal with the ugly blotches from resolving the
                // complicated geometry of the inside of the brush pen with the
                // clear case
                m.opacity = m._defaultOpacity * Math.sqrt(opacity);
            }
            if (m._isPenColor) {
                m.color.fromArray(color, 0);
                m.color.convertSRGBToLinear();
            }
        }
    }
}

function dumpObject3D(ob, level=0) {
    const type = ob.constructor.name;
    const info = `${type}: "${ob.name}"`;
    const indent = ''.padEnd(2 * level, ' ');
    console.log(indent + info);
    if (ob.material) {
        const materials = (
            Array.isArray(ob.materials) ? ob.materials : [ob.material]);
        materials.forEach(m => dumpObject3D(m, level + 1));
    }
    ob.children?.forEach(c => dumpObject3D(c, level + 1));
}

function findObject(object, predicate) {
    if (predicate(object))
        return object;

    if (!object.children)
        return;

    for (let child of object.children) {
        const result = findObject(child, predicate);
        if (result)
            return result;
    }

    return null;
}

function calcPenMatrix(matrix, dir, tiltXYRotate, lift) {
    const theta = atan2(-dir[0], -dir[2]);
    const phi = asin(dir[1]);

    mat4.identity(matrix);

    // direction
    const DEG2RAD = PI / 180;
    mat4.rotateY(matrix, matrix, theta);
    mat4.rotateX(matrix, matrix, phi);

    // XXX lift
    //const z = 10 - 0.2 * smoothstep(0, 1, lift);
    //const z = 5;
    mat4.translate(matrix, matrix, [0, 0, -10]);

    // align model
    mat4.rotateX(matrix, matrix, PI/2);

    // rotate
    //const rotate = 0;
    const rotate = tiltXYRotate[2];
    mat4.rotateY(matrix, matrix, PI * rotate);

    // tilt
    //const tiltX = 0.26;
    //const tiltY = 0.36;
    const tiltX = tiltXYRotate[0];
    const tiltY = tiltXYRotate[1];
    mat4.rotateX(matrix, matrix, PI/2 * -tiltY);
    mat4.rotateZ(matrix, matrix, PI/2 * -tiltX);

    // logo
    mat4.rotateY(matrix, matrix, DEG2RAD * 50);

    mat4.translate(matrix, matrix, [0, lift, 0]);

    // scale
    const scale = 0.05;
    mat4.scale(matrix, matrix, [scale, scale, scale]);

    return matrix;
}
